var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Light_1;
import { TrackedVec3, vector3_pool } from '../../../../Math.js';
import { IColor, LightType } from '../../../../api.js';
import { calculate_world_transform, calculate_transform_from_parent_world_transform } from '../../../utility/TransformHelper.js';
import { vector_3 } from '../../../utility/Conversion.js';
import { Observable } from '../../../../Observable.js';
import { Deprecated, deprecate } from '../../../../utils/Deprecated.js';
import { Debug } from '../../../../utils/DebugLayer/utils/DebugDecorators.js';
import { Logger } from '../../../../logging/Logger.js';
const LOGGER_CATEGORY = 'Light';
// TODO: Remove this once we do a breaking change
const vector_to_color = deprecate(function to_color(vector) {
    return { r: vector.x, g: vector.y, b: vector.z };
}, 'setting color using IVector3 is deprecated');
export class ILight {
    static is_point_light(light) {
        return light.type === LightType.POINT_LIGHT;
    }
    static is_directional_light(light) {
        return light.type === LightType.DIRECTIONAL_LIGHT;
    }
    static is_spot_light(light) {
        return light.type === LightType.SPOT_LIGHT;
    }
    static is_line_light(light) {
        return light.type === LightType.LINE_LIGHT;
    }
    static is_directed_shadow_light(light) {
        return light.type === LightType.DIRECTED_SHADOW_POINT_LIGHT;
    }
}
/**
 * An abstract light
 */
let Light = Light_1 = class Light {
    constructor(_viewer) {
        this._viewer = _viewer;
        this.update_observable = new Observable();
        this.shadow = null;
        this._ready = Promise.resolve();
        this._intensity = 1.0;
        this._intensity_affects_parent_emission_color = false;
        this._position = new TrackedVec3();
        this._color = new TrackedVec3(1, 1, 1);
        this._dirty_flags = Light_1.DIRTY_EVERYTHING;
        this._parent = null;
        this._parent_material = null;
        this._debug_geometry = null;
        this._light_system = _viewer.light_system;
        this._position.onchange = this.mark_dirty.bind(this, Light_1.DIRTY_POSITION);
        this._color.onchange = this.mark_dirty.bind(this, Light_1.DIRTY_COLOR);
        this.id = Light_1._id_count++;
    }
    get name() {
        return this._babylon_light.name;
    }
    set name(name) {
        this._babylon_light.name = name;
    }
    get position() {
        return this._position;
    }
    set position(value) {
        this._position.set(value.x, value.y, value.z);
    }
    get color() {
        return this._color;
    }
    set color(value) {
        const color = IColor.is(value) ? value : vector_to_color(value);
        if (this._viewer.get_config().verbose_warnings && !IColor.is_saturated(color)) {
            Logger.as(LOGGER_CATEGORY).warn('Setting color with one or more channel not in range 0-1, this is generally a bad idea');
        }
        this._color.set(color.r, color.g, color.b);
    }
    // ------ ILight interface implementation -------
    get intensity_affects_parent_emission_color() {
        return this._intensity_affects_parent_emission_color;
    }
    set intensity_affects_parent_emission_color(value) {
        if (this._intensity_affects_parent_emission_color === value)
            return;
        this._intensity_affects_parent_emission_color = value;
        this.mark_dirty(Light_1.DIRTY_PARENT_EMISSIVE_COLOR);
    }
    get intensity() {
        return this._intensity;
    }
    set intensity(i) {
        if (this._intensity === i)
            return;
        this._intensity = i;
        this.mark_dirty(Light_1.DIRTY_INTENSITY);
    }
    set parent(object_id) {
        if (this.parent === object_id)
            return;
        const scene_object = object_id !== null ? this._viewer.viewer_scene.objects.get(object_id) : null;
        if (this._parent !== scene_object) {
            if (scene_object !== null) {
                this.set_parent(scene_object);
            }
            else {
                this.remove_parent();
            }
        }
    }
    get parent() {
        var _a, _b;
        return (_b = (_a = this._parent) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    }
    get_world_position() {
        if (!this._parent) {
            return this._position;
        }
        const parent_world_transform = calculate_world_transform(this._parent);
        const new_transform = calculate_transform_from_parent_world_transform(parent_world_transform, vector_3(this._position));
        return new_transform.position;
    }
    set_color_temperature(degrees_kelvin) {
        if (degrees_kelvin < 1000 || degrees_kelvin > 40000) {
            Logger.as(LOGGER_CATEGORY).error('Color temperature value outside of accepted range [1000-40000]. Color accuracy may vary outside of values 1000-40000.');
        }
        const color = IColor.from_temperature(degrees_kelvin); // Color values in range [0, 1] in sRGB color space.
        // Convert color to linear color space to be able to normalize the sum of the vector's components (to maintain the same intensity regardless of color temp)
        const linear_color = vector3_pool.fetch();
        linear_color.set(Math.pow(color.r, 2.2), Math.pow(color.g, 2.2), Math.pow(color.b, 2.2));
        const vector_sum = linear_color.x + linear_color.y + linear_color.z;
        linear_color.set(linear_color.x / vector_sum, linear_color.y / vector_sum, linear_color.z / vector_sum);
        // Convert color back to sRGB color space when setting the normalized color values.
        this._color.set(Math.pow(linear_color.x, 1 / 2.2), Math.pow(linear_color.y, 1 / 2.2), Math.pow(linear_color.z, 1 / 2.2));
        vector3_pool.return(linear_color);
    }
    remove_debug_geometry() {
        if (this._debug_geometry) {
            this._debug_geometry.dispose();
            this._debug_geometry = null;
        }
    }
    get debug_geometry() {
        return this._debug_geometry;
    }
    get ready() {
        return this._ready;
    }
    dispose() {
        var _a;
        this.remove_debug_geometry();
        if (this._babylon_light) {
            this._viewer.scene.removeLight(this._babylon_light);
            this._babylon_light.dispose();
        }
        this._light_system.remove_light(this);
        (_a = this.shadow) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    mark_dirty(flags) {
        this._dirty_flags |= flags;
    }
    is_dirty(mask = Light_1.DIRTY_EVERYTHING) {
        return !!(this._dirty_flags & mask);
    }
    clear_dirty_flags() {
        this._dirty_flags = 0;
    }
    unset_dirty_flags(mask) {
        this._dirty_flags &= ~mask;
    }
    set_parent(scene_object) {
        if (this._parent) {
            this.remove_parent();
        }
        this._parent = scene_object;
        this.mark_dirty(Light_1.DIRTY_PARENT_ADDED);
    }
    remove_parent() {
        this._parent = null;
        this.mark_dirty(Light_1.DIRTY_PARENT_REMOVED);
    }
    update_color_and_intensity() {
        if (this.is_dirty(Light_1.DIRTY_INTENSITY)) {
            this._babylon_light.intensity = this.intensity;
        }
        if (this.is_dirty(Light_1.DIRTY_COLOR)) {
            // Convert the color values from sRGB color space to linear color space when sending the color values to Babylon.
            this._babylon_light.diffuse.set(Math.pow(this._color.r, 2.2), Math.pow(this._color.g, 2.2), Math.pow(this._color.b, 2.2));
            this._babylon_light.specular.set(Math.pow(this._color.r, 2.2), Math.pow(this._color.g, 2.2), Math.pow(this._color.b, 2.2));
        }
    }
    update_parent_material() {
        var _a, _b;
        if (this.is_dirty(Light_1.DIRTY_PARENT_ADDED)) {
            const material_id = (_a = this._parent) === null || _a === void 0 ? void 0 : _a.get_material();
            if (material_id) {
                this._parent_material = this._viewer.asset_manager.materials.get(material_id);
            }
        }
        else if (this.is_dirty(Light_1.DIRTY_PARENT_REMOVED) && this._parent_material) {
            (_b = this._parent_material) === null || _b === void 0 ? void 0 : _b.reset_emissive_color();
            this._parent_material = null;
        }
        if (this._parent_material) {
            const affects_parent = this.intensity_affects_parent_emission_color;
            const parent_color_is_dirty = this.is_dirty(Light_1.DIRTY_INTENSITY | Light_1.DIRTY_COLOR | Light_1.DIRTY_PARENT_EMISSIVE_COLOR);
            if (affects_parent && parent_color_is_dirty) {
                this.update_parent_emissive_color();
            }
            else if (!affects_parent) {
                this._parent_material.reset_emissive_color();
            }
        }
    }
    update_parent_emissive_color() {
        if (!this._parent)
            return;
        const material_id = this._parent.get_material();
        const parent_material = (material_id !== null)
            ? this._viewer.asset_manager.materials.get(material_id)
            : null;
        if (parent_material) {
            // With a value of 0 we will get zero emission (Climate neutral) earlier and therefore will not have the material shown in the glow layer.
            parent_material.emissive_color = new IColor(Math.pow(Math.pow(this._color.r, 2.2) * this.intensity, 1.0 / 2.2), Math.pow(Math.pow(this._color.g, 2.2) * this.intensity, 1.0 / 2.2), Math.pow(Math.pow(this._color.b, 2.2) * this.intensity, 1.0 / 2.2));
        }
    }
};
Light._id_count = 0;
// Lets use the lower 24 bits (second half of the literal) for the common stuff in this base class.
// The upper 8 bits can be used by each light type that extends the base.
// Bitmasks for the properties in the derived classes can overlap (e.g. DirectionLight.DIRTY_DIRECTION and SpotLight.DIRTY_ANGLE can use the same bitmask)
// TODO: Switch linter to eslint and allow aligning stuff like this using whitespace
Light.DIRTY_POSITION = 1;
Light.DIRTY_INTENSITY = 2;
Light.DIRTY_COLOR = 4;
Light.DIRTY_PARENT_EMISSIVE_COLOR = 8;
Light.DIRTY_PARENT_TRANSFORM = 16;
Light.DIRTY_PARENT_ADDED = 32;
Light.DIRTY_PARENT_REMOVED = 64;
Light.DIRTY_ENABLE_SHADOWS = 256;
Light.DIRTY_SHADOW_BIAS = 512;
Light.DIRTY_SHADOW_NEAR = 1024;
Light.DIRTY_SHADOW_FAR = 2048;
Light.DIRTY_SHADOW_RESOLUTION = 4096;
Light.DIRTY_SHADOW_MAP = 8192;
Light.DIRTY_DIRECTION = 16777216;
Light.DIRTY_ANGLE = 33554432;
// TODO: This flag is a bit broken by design since it sets that a parent was both added and removed.
// This is some kind of broken state which makes it hard to reason about, since we expect add and remove to be mutually exclusive
Light.DIRTY_EVERYTHING = 4294967295;
__decorate([
    Deprecated
], Light.prototype, "intensity_affects_parent_emission_color", null);
Light = Light_1 = __decorate([
    Debug.Selectable({
        icon: '☀',
        name: 'Light',
        tooltip: (light) => light.type,
        world_position: light => light.get_world_position()
    })
], Light);
export { Light };
//# sourceMappingURL=Light.js.map